import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useViewsStore } from '@/stores/views.store'
import { useUpdateView, viewsKeys } from '@/hooks/use-views'
import type {
  ViewEntityType,
  CreateViewColumn,
  CreateViewFilter,
  CreateViewSort,
} from '@/types/api'

interface UseViewActionsProps {
  entityType: ViewEntityType
  viewId: string | null
}

interface SaveViewPayload {
  filters?: CreateViewFilter[]
  sorts?: CreateViewSort[]
  columns?: CreateViewColumn[]
}

/**
 * Hook for handling view save/revert actions for filters, sorts, and columns.
 * All changes require explicit save via handleSaveView.
 */
export function useViewActions({ entityType, viewId }: UseViewActionsProps) {
  const queryClient = useQueryClient()
  const { mutate: updateView, isPending: isUpdating } =
    useUpdateView(entityType)

  const getWorkingState = useViewsStore((state) => state.getWorkingState)
  const resetWorkingState = useViewsStore((state) => state.resetWorkingState)
  const setHasPendingColumnChanges = useViewsStore(
    (state) => state.setHasPendingColumnChanges
  )

  /**
   * Save the current filter/sort/column state to the view.
   * @param payload - Optional payload to override working state values
   * @param payload.columns - Column state from getCurrentColumns() in use-datatable
   */
  const handleSaveView = useCallback(
    (payload?: SaveViewPayload) => {
      if (!viewId) return

      const workingState = getWorkingState(viewId)

      // Build filters payload from working state if not provided
      const filters: CreateViewFilter[] =
        payload?.filters ??
        (workingState?.filters.map((f, index) => ({
          fieldName: f.field,
          operator: mapOperator(f.op),
          value: f.value,
          filterOrder: index,
        })) ||
          [])

      // Build sorts payload from working state if not provided
      const sorts: CreateViewSort[] =
        payload?.sorts ??
        (workingState?.sorts.map((s, index) => ({
          fieldName: s.field,
          direction: s.direction.toUpperCase() as 'ASC' | 'DESC',
          sortOrder: index,
        })) ||
          [])

      // Build update data - only include columns if provided
      const updateData: {
        filters: CreateViewFilter[]
        sorts: CreateViewSort[]
        columns?: CreateViewColumn[]
      } = {
        filters,
        sorts,
      }

      // Include columns if provided (from getCurrentColumns in use-datatable)
      if (payload?.columns) {
        updateData.columns = payload.columns
      }

      updateView(
        {
          id: viewId,
          data: updateData,
        },
        {
          onSuccess: () => {
            toast.success('View updated')
            // Reset pending column changes flag
            setHasPendingColumnChanges(viewId, false)
            // Invalidate to refetch fresh data
            queryClient.invalidateQueries({
              queryKey: viewsKeys.detail(viewId),
            })
          },
          onError: (error) => {
            console.error('Failed to update view:', error)
            toast.error('Failed to update view')
          },
        }
      )
    },
    [
      viewId,
      getWorkingState,
      updateView,
      queryClient,
      setHasPendingColumnChanges,
    ]
  )

  /**
   * Revert all changes (filters, sorts, columns) to the saved view state.
   */
  const handleRevertChanges = useCallback(() => {
    if (!viewId) return
    resetWorkingState(viewId)
  }, [viewId, resetWorkingState])

  return {
    handleSaveView,
    handleRevertChanges,
    isUpdating,
  }
}

/**
 * Map internal filter operator to API operator format
 */
function mapOperator(
  op: string
):
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'IN'
  | 'NOT_IN'
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'GREATER_THAN'
  | 'GREATER_THAN_OR_EQUAL'
  | 'LESS_THAN'
  | 'LESS_THAN_OR_EQUAL'
  | 'IS_NULL'
  | 'IS_NOT_NULL' {
  const operatorMap: Record<string, ReturnType<typeof mapOperator>> = {
    eq: 'EQUALS',
    neq: 'NOT_EQUALS',
    in: 'IN',
    notIn: 'NOT_IN',
    contains: 'CONTAINS',
    notContains: 'NOT_CONTAINS',
    gt: 'GREATER_THAN',
    gte: 'GREATER_THAN_OR_EQUAL',
    lt: 'LESS_THAN',
    lte: 'LESS_THAN_OR_EQUAL',
    isNull: 'IS_NULL',
    isNotNull: 'IS_NOT_NULL',
  }
  return operatorMap[op] || 'EQUALS'
}
